<template>
  <ts-card>
    <div class="form-header ts-bg-gray-100 ts-border-b ts-border-gray-400 ts-p-4 ts-relative">
      <h4 class="ts-text-sm">{{ $t('payMonthlyDeduction.pageTitle') }}</h4>
      <ts-modal-close-button />
    </div>
    <ts-card-wrapper>
      <ts-loading-banner :loading="loadingPreData">
        <form>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group m-b-10">
                <label class="col-form-label control-label required">{{ $t('payMonthlyDeduction.employeeId') }}</label>
                <div>
                  <ts-select
                    v-model="model.employee_id"
                    :options="
                      employees.map(e => ({
                        text: e.employee_name_en,
                        value: e.employee_id
                      }))
                    "
                    :class="{ 'is-invalid': errors.has('employee_id') }"
                  />
                  <div class="invalid-feedback" v-if="errors.has('employee_id')">{{ errors.first('employee_id') }}</div>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group m-b-10">
                <label class="col-form-label control-label required">{{ $t('payMonthlyDeduction.deductionId') }}</label>
                <div>
                  <ts-select
                    v-model="model.deduction_id"
                    :options="
                      deductions.map(e => ({
                        text: e.deduction_item,
                        value: e.deduction_id
                      }))
                    "
                    :class="{ 'is-invalid': errors.has('deduction_id') }"
                  />
                  <div class="invalid-feedback" v-if="errors.has('deduction_id')">
                    {{ errors.first('deduction_id') }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group m-b-10">
                <label class="col-form-label control-label required">{{ $t('payMonthlyDeduction.deduction') }}</label>
                <div>
                  <ts-input
                    type="number"
                    v-model.number="model.deduction"
                    :class="{ 'is-invalid': errors.has('deduction') }"
                  />
                  <div class="invalid-feedback" v-if="errors.has('deduction')">{{ errors.first('deduction') }}</div>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group m-b-10">
                <label class="col-form-label control-label required">{{ $t('payMonthlyDeduction.cycleYear') }}</label>
                <div>
                  <ts-input v-model="model.cycle_year" :class="{ 'is-invalid': errors.has('cycle_year') }" />
                  <div class="invalid-feedback" v-if="errors.has('cycle_year')">{{ errors.first('cycle_year') }}</div>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group m-b-10">
                <label class="col-form-label control-label required">{{ $t('payMonthlyDeduction.cycleMonth') }}</label>
                <div>
                  <ts-input v-model="model.cycle_month" :class="{ 'is-invalid': errors.has('cycle_month') }" />
                  <div class="invalid-feedback" v-if="errors.has('cycle_month')">{{ errors.first('cycle_month') }}</div>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group m-b-10">
                <label class="col-form-label control-label">{{ $t('payMonthlyDeduction.description') }}</label>
                <div>
                  <ts-textarea v-model="model.description" rows="3" />
                </div>
              </div>
            </div>
          </div>
          <div class="row ts-mt-6">
            <div class="col-sm-12 col-lg-12 col-md-12 ts-text-right">
              <ts-button @click.prevent="$emit('close')" :disabled="saving">{{ $t('button.cancel') }}</ts-button>
              <ts-button
                outline
                color="primary"
                class="ml-2"
                :waiting="savingAndNew"
                @click.prevent="saveAndNew"
                v-if="editData === undefined"
                >{{ $t('button.createAndNew') }}</ts-button
              >
              <ts-button color="primary" class="ml-2" :waiting="saving" @click.prevent="save">
                {{ editData ? $t('button.update') : $t('button.create') }}
              </ts-button>
            </div>
          </div>
        </form>
      </ts-loading-banner>
    </ts-card-wrapper>
  </ts-card>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { cloneDeep } from 'lodash'
import { mapState, mapActions } from 'vuex'

export default {
  name: 'pay-monthly-deduction-form',
  props: ['editData'],
  data() {
    const model = {
      employee_id: this.editData ? this.editData.employee_id : '',
      deduction_id: this.editData ? this.editData.deduction_id : '',
      deduction: this.editData ? this.editData.deduction : '',
      cycle_year: this.editData ? this.editData.cycle_year : '',
      cycle_month: this.editData ? this.editData.cycle_month : '',
      description: this.editData ? this.editData.description : ''
    }

    return {
      newModel: model,
      model: cloneDeep(model),
      errors: new Errors(),
      saving: false,
      savingAndNew: false,
      loadingPreData: false
    }
  },
  computed: {
    ...mapState('hr/payMonthlyDeduction', ['employees', 'deductions']),
    dayRange: vm => (start, end) => {
      return range(start, end)
    }
  },
  created() {
    this.loadingPreData = true

    Promise.all([this.fetchEmployees(), this.fetchDeductions()]).then(responses => {
      this.loadingPreData = false
    })
  },
  methods: {
    ...mapActions('hr/payMonthlyDeduction', ['fetchEmployees', 'fetchDeductions']),
    submit() {
      let responseResult

      this.errors = new Errors()

      if (this.editData) {
        responseResult = this.$store.dispatch('hr/payMonthlyDeduction/update', {
          id: this.editData.record_id,
          ...this.model
        })
      } else {
        responseResult = this.$store.dispatch('hr/payMonthlyDeduction/create', this.model)
      }

      return responseResult
        .then(response => {
          return this.$store.dispatch('hr/payMonthlyDeduction/fetch').then(res => {
            this.saving = false
            this.$toasted.success(response.message)
            return Promise.resolve(res)
          })
        })
        .catch(error => {
          if (error.errors) this.errors = new Errors(error.errors)
          else this.$toasted.error(error.message)

          return Promise.reject(error)
        })
    },
    save() {
      this.saving = true

      this.submit()
        .then(() => {
          this.$emit('close')
        })
        .finally(() => {
          this.saving = false
        })
    },
    saveAndNew() {
      this.savingAndNew = true

      this.submit()
        .then(() => {
          this.model = cloneDeep(this.newModel)
        })
        .finally(() => {
          this.savingAndNew = false
        })
    }
  }
}
</script>
